"use client"

import { Popover, PopoverButton } from "@headlessui/react"
import { HttpTypes } from "@medusajs/types"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"

import CartMobileModal from "../cart-mobile-modal"
import CartMobileItemsOverlay from "../cart-mobile-modal/cart-mobile-items-overlay"
import { listCartPaymentMethods } from "@lib/data/payment"

const CartDropdown = ({
  cart: cartState,
  shippingOptions = [],
}: {
  cart?: HttpTypes.StoreCart | null
  shippingOptions?: HttpTypes.StoreCartShippingOption[]
}) => {
  const [activeTimer, setActiveTimer] =
    useState<NodeJS.Timeout | undefined>(undefined)

  const [mobileModalOpen, setMobileModalOpen] = useState(false)
  const [isFixed, setIsFixed] = useState(false)

  const [paymentMethods, setPaymentMethods] =
    useState<HttpTypes.StorePaymentProvider[]>([])

  const totalItems =
    cartState?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0

  const itemDiff = useRef(totalItems)

  const pathname = usePathname()

  const isMobileViewport = () => {
    return typeof window !== "undefined" && window.innerWidth < 640
  }

  useEffect(() => {
    async function loadPaymentMethods() {
      if (!cartState?.region_id) return

      const methods = await listCartPaymentMethods(cartState.region_id)

      setPaymentMethods(methods || [])
    }

    loadPaymentMethods()
  }, [cartState?.region_id])

  const openModal = () => setMobileModalOpen(true)

  const closeModal = () => {
    if (!isFixed) {
      setMobileModalOpen(false)
    }
  }

  const closeEverything = () => {
    setMobileModalOpen(false)
    setIsFixed(false)
  }

  const timedOpen = () => {
    openModal()

    if (activeTimer) clearTimeout(activeTimer)

    const timer = setTimeout(() => {
      closeModal()
    }, 5000)

    setActiveTimer(timer)
  }

  const handleMouseEnter = () => {
    if (isMobileViewport()) return

    if (activeTimer) clearTimeout(activeTimer)

    openModal()
  }

  useEffect(() => {
    if (totalItems > itemDiff.current && pathname !== "/cart" && !isMobileViewport()) {
      timedOpen()
    }

    itemDiff.current = totalItems
  }, [totalItems, pathname])

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault()

    if (isMobileViewport()) {
      setMobileModalOpen(true)
      return
    }

    const newFixedState = !isFixed

    setIsFixed(newFixedState)

    if (newFixedState) {
      openModal()
    } else {
      setMobileModalOpen(false)
    }
  }

  return (
    <div
      className="h-full z-50"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={closeModal}
    >
      <Popover className="relative h-full">
        <PopoverButton
          className="group relative flex h-full items-center justify-center px-1 focus:outline-none"
          onClick={handleButtonClick}
          aria-label={`Cart (${totalItems})${isFixed ? " fixed" : ""}`}
        >
          <span
            className="
              relative flex h-11 w-11 items-center justify-center
              rounded-full
              border border-white/70
              bg-gradient-to-b from-[#3a5f88] via-[#284469] to-[#1b2b46]
              shadow-[0_6px_10px_rgba(0,0,0,0.35),0_2px_4px_rgba(0,0,0,0.25)]
              ring-1 ring-white/20
              transition-all duration-300
              group-hover:scale-105 group-hover:brightness-110
              group-active:scale-[0.96]
            "
          >
            <span
              className="
                pointer-events-none absolute inset-0 rounded-full
                bg-gradient-to-b from-white/30 via-white/10 to-transparent
              "
            />

            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="relative z-10 h-6 w-6 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
            >
              <path d="M2.25 3A.75.75 0 0 1 3 2.25h1.386c.334 0 .623.221.709.543l.525 1.957H20.25a.75.75 0 0 1 .728.93l-1.5 6A.75.75 0 0 1 18.75 12H7.31l.4 1.5H18a.75.75 0 0 1 0 1.5H7.125a.75.75 0 0 1-.724-.555L4.04 5.25H3A.75.75 0 0 1 2.25 4.5V3Zm6.75 15a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm8.25 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z" />
            </svg>

            {totalItems > 0 && (
              <span
                className="
                  absolute -right-1 -top-1 z-20
                  flex min-h-[22px] min-w-[22px] items-center justify-center
                  rounded-full
                  border border-white/90
                  bg-gradient-to-b from-[#f7a23a] to-[#f28c1b]
                  px-1
                  text-[14px] font-extrabold leading-none text-white
                  shadow-[0_4px_8px_rgba(0,0,0,0.28)]
                "
              >
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}

            {isFixed && (
              <span
                className="
                  absolute -left-0.5 -bottom-0.5 z-20
                  h-3 w-3 rounded-full
                  border border-white/80 bg-[#32a852]
                  shadow-[0_2px_6px_rgba(0,0,0,0.25)]
                "
              />
            )}
          </span>
        </PopoverButton>
      </Popover>

      {mobileModalOpen && (
        isMobileViewport() ? (
          <CartMobileModal
            open={mobileModalOpen}
            onClose={closeEverything}
            cart={cartState}
            shippingOptions={shippingOptions}
            paymentMethods={paymentMethods}
          />
        ) : (
          <CartMobileItemsOverlay
            open={mobileModalOpen}
            onClose={closeEverything}
            cart={cartState || null}
          />
        )
      )}
    </div>
  )
}

export default CartDropdown
